import { FolderPlus } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import Link from '@/routing/Link'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { ROUTES } from '@/routes/routes'
import type { AudienceLibraryActionsProps } from '@/types/components/library/audience-library-actions'
import { AddToCollectionMenu } from './AddToCollectionMenu'
import { useAudienceLibraryState } from './useAudienceLibraryState'

function AudienceLibraryPicker({ audienceToken, inMenu }: AudienceLibraryActionsProps) {
  const { t } = useTranslation('app')
  const { data, error } = useAudienceLibraryState(audienceToken)
  if (!data || error || data.inLibrary) return null
  const label = t('library.audience.addToLibrary')
  const content = <><FolderPlus className="h-4 w-4" aria-hidden="true" />{label}</>
  return (
    <AddToCollectionMenu
      items={[{ kind: 'audience', token: audienceToken }]}
      testId="audience-add-to-library"
      trigger={inMenu ? (
        <DropdownMenuItem onSelect={event => event.preventDefault()} data-testid="audience-add-to-library-trigger">
          {content}
        </DropdownMenuItem>
      ) : (
        <Button type="button" variant="outline" size="sm" className="gap-1.5" data-testid="audience-add-to-library-trigger">
          {content}
        </Button>
      )}
    />
  )
}

export function AudienceAddToLibrary(props: AudienceLibraryActionsProps) {
  const enabled = useAssistantPlatformEnabled()
  return enabled ? <AudienceLibraryPicker {...props} /> : null
}

function AudienceCollectionControl(props: AudienceLibraryActionsProps) {
  const { data, error } = useAudienceLibraryState(props.audienceToken)
  if (!data || error) return null
  return data.inLibrary
    ? <AddToCollectionMenu items={[{ kind: 'audience', token: props.audienceToken }]} testId="audience-add-to-collection" />
    : <AudienceLibraryPicker {...props} />
}

export function AudienceLibraryCollections(props: AudienceLibraryActionsProps) {
  const enabled = useAssistantPlatformEnabled()
  return enabled ? <AudienceCollectionControl {...props} /> : null
}

function AudienceSourceRow({ audienceToken }: AudienceLibraryActionsProps) {
  const { t } = useTranslation('app')
  const { data } = useAudienceLibraryState(audienceToken)
  const source = data?.sourceDocument
  if (!source) return null
  return (
    <Text as="div" size="sm" color="muted" className="flex flex-wrap items-center gap-2" data-testid="audience-source-dataset">
      <span>{t('library.audience.sourceDataset')}</span>
      <Link href={ROUTES.knowledgeDocument(source.token)} className="text-foreground underline underline-offset-2">{source.name}</Link>
    </Text>
  )
}

export function AudienceSourceDataset(props: AudienceLibraryActionsProps) {
  const enabled = useAssistantPlatformEnabled()
  return enabled ? <AudienceSourceRow {...props} /> : null
}
